import { isPremiumChatLocked } from '../../utils/premiumChatAccess';
/* eslint-disable jsx-a11y/anchor-has-content */
import React, { useEffect } from 'react';
import 'katex/dist/katex.min.css';
import './ChatWindow.css';

import { isDeepSeekReasoning } from '../../chatModels';
import ImageModal from '../ImageModal';
import ChatWindowContent from './ChatWindowContent';
import { useAutoScroll } from '../../hooks/ui/useAutoScroll';
import { useTextToSpeech } from '../../hooks/shared/useTextToSpeech';
import { useMessageHandlers } from '../../hooks/chat/useMessageHandlers';
import { useReasoningTimer } from '../../hooks/chat/useReasoningTimer';
import { useImageModal } from '../../hooks/ui/useImageModal';
import { useModels } from '../../contexts/ModelContext';

const ChatWindow = ({
  selectedChat,
  messages,
  selectedModel,
  isLoadingMessages,
  onQuestionClick,
  onArtifactOpen,
  onEditMessage,
  onMessageSent,
  updateChatTitle,
  hasSubscription,
  messageLimit,
  editingMessage,
  onCancelEdit,
  applyEditAndTruncate,
}) => {
  const { models, getModelByModelString } = useModels();
  const isChatLocked = isPremiumChatLocked(selectedChat, messages, models, hasSubscription, [selectedModel]);
  // Custom hooks
  const {
    chatContainerRef,
    hasUserSentMessage,
    newMessageId,
  } = useAutoScroll(messages, selectedChat);
  
  const {
    isSpeaking,
    speakingMessageId,
    audioSource,
    audioRef,
    handleSpeak
  } = useTextToSpeech();
  
  const {
    hoveredMessageId,
    setHoveredMessageId,
    copyStatus,
    showReasoningMap,
    setShowReasoningMap,
    handleCopyMessage,
    toggleReasoning
  } = useMessageHandlers();

  const {
    reasoningTimers,
    startThinkingTimer,
    stopThinkingTimer
  } = useReasoningTimer();

  const {
    imageModal,
    openImageModal,
    closeImageModal
  } = useImageModal();

  const chatModel = getModelByModelString(selectedModel);
  const isDeepSeekReasoningSelected = isDeepSeekReasoning(chatModel);

  // Expand reasoning while a new assistant response is live. Saved history
  // starts collapsed so old chats stay compact by default.
  useEffect(() => {
    const newMap = { ...showReasoningMap };
    for (const msg of messages) {
      if (
        msg.role === 'assistant' &&
        msg.reasoning_text &&
        msg.streaming &&
        !(msg.id in newMap)
      ) {
        newMap[msg.id] = true;
      }
    }
    if (Object.keys(newMap).length !== Object.keys(showReasoningMap).length) {
      setShowReasoningMap(newMap);
    }
  }, [messages, showReasoningMap, setShowReasoningMap]);

  // Restore thinking timer effect
  useEffect(() => {
    messages.forEach((msg) => {
      if (msg.role !== 'assistant') return;
      if (!msg.streaming) { stopThinkingTimer(msg.id); return; }
  
      const hasReasoningText = !!msg.reasoning_text;
      const hasMainText = !!msg.text;
  
      // If we just got reasoning text and no main text => start timer
      if (hasReasoningText && !hasMainText) {
        startThinkingTimer(msg.id);
      }
  
      // If we have main text => stop timer
      // or if streaming is false => also stop, in case there's partial text
      if (hasMainText || !msg.streaming) {
        stopThinkingTimer(msg.id);
      }
    });
  }, [messages, startThinkingTimer, stopThinkingTimer]);

  return (
    <div className="flex flex-col h-full relative">
      <ChatWindowContent
        messages={messages}
        selectedChat={selectedChat}
        isLoadingMessages={isLoadingMessages}
        onQuestionClick={onQuestionClick}
        chatContainerRef={chatContainerRef}
        hasUserSentMessage={hasUserSentMessage}
        newMessageId={newMessageId}
        hoveredMessageId={hoveredMessageId}
        setHoveredMessageId={setHoveredMessageId}
        copyStatus={copyStatus}
        isSpeaking={isSpeaking}
        speakingMessageId={speakingMessageId}
        showReasoningMap={showReasoningMap}
        reasoningTimers={reasoningTimers}
        isDeepSeekReasoningSelected={isDeepSeekReasoningSelected}
        onSpeak={handleSpeak}
        onCopy={handleCopyMessage}
        onArtifactOpen={onArtifactOpen}
        onImageClick={openImageModal}
        onToggleReasoning={toggleReasoning}
        onEditMessage={isChatLocked ? undefined : onEditMessage}
        editingMessage={isChatLocked ? null : editingMessage}
        onCancelEdit={onCancelEdit}
        selectedModel={selectedModel}
        onMessageSent={onMessageSent}
        updateChatTitle={updateChatTitle}
        hasSubscription={hasSubscription}
        messageLimit={messageLimit}
        applyEditAndTruncate={applyEditAndTruncate}
      />

      {/* Hidden audio element to play TTS */}
      <div className="hidden">
        <audio ref={audioRef} src={audioSource || ''} controls autoPlay />
      </div>

      {/* Image Modal */}
      <ImageModal
        isOpen={imageModal.isOpen}
        imageUrl={imageModal.imageUrl}
        alt={imageModal.alt}
        startRect={imageModal.startRect}
        onClose={closeImageModal}
      />
    </div>
  );
};

// Add this style block right after your existing imports
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes fade-in-up {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .animate-fade-in-up {
    animation: fade-in-up 0.5s ease-out forwards;
    opacity: 0;
  }

  @keyframes message-in {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .animate-message-in {
    animation: message-in 0.3s ease-out forwards;
  }
`;
document.head.appendChild(styleSheet);

export default React.memo(ChatWindow);
